import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Headset, X, Send, User, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSupportChat } from '../hooks/useSupportChat';
import { ref, onValue } from 'firebase/database';
import { db } from '../lib/firebase';

export default function SupportChatWidget() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);

  const {
    messages,
    typing,
    sendMessage,
    updateTypingStatus,
    markAsRead,
    initializeChatInfo,
  } = useSupportChat(currentUser?.uid);

  // Listen to unread count
  useEffect(() => {
    if (!currentUser) return;

    const unreadRef = ref(db, `support-chats/${currentUser.uid}/unreadByUser`);
    const unsubscribe = onValue(unreadRef, (snapshot) => {
      setUnreadCount(snapshot.val() || 0);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Mark as read when opened
  useEffect(() => {
    if (isOpen && currentUser && unreadCount > 0) {
      markAsRead(false);
    }
  }, [isOpen, currentUser, unreadCount, markAsRead]);

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;

    // Initialize chat info if first message
    if (messages.length === 0) {
      await initializeChatInfo();
    }

    await sendMessage(messageText.trim(), false);
    setMessageText('');
  };

  const handleTyping = () => {
    updateTypingStatus(true, false);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Hide support chat on admin pages and meet pages
  if (!currentUser || location.pathname.startsWith('/operations-control-center') || location.pathname.startsWith('/meet/')) {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-all duration-200 flex items-center justify-center z-50"
      >
        <Headset className="h-7 w-7" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Chat Widget */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 h-[600px] bg-white rounded-lg shadow-2xl flex flex-col z-50 animate-in slide-in-from-bottom-4 duration-200">
          {/* Header */}
          <div className="bg-purple-600 text-white px-4 py-3 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center">
              <Headset className="h-5 w-5 mr-2" />
              <h3 className="font-semibold">Support Chat</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-purple-700 rounded p-1 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <Headset className="h-16 w-16 text-purple-300 mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Need Help?
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Our support team is here to assist you. Send us a message and
                  we'll respond as soon as possible.
                </p>
                <div className="text-xs text-gray-500 space-y-1 mb-4">
                  <p>• Response time: Usually within minutes</p>
                  <p>• Available: Mon-Fri, 8AM-6PM PST</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                  <p className="text-xs text-blue-700 flex items-center justify-center">
                    <Info className="h-3 w-3 mr-1.5" />
                    Chat history is deleted after 7 days of inactivity
                  </p>
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${
                      msg.isAdmin ? 'justify-start' : 'justify-end'
                    }`}
                  >
                    <div
                      className={`max-w-[80%] ${
                        msg.isAdmin ? 'bg-blue-50' : 'bg-purple-100'
                      } rounded-lg p-3`}
                    >
                      {msg.isAdmin && (
                        <div className="flex items-center mb-1">
                          <User className="h-3 w-3 mr-1 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-600">
                            Support Team
                          </span>
                        </div>
                      )}
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                        {msg.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTimestamp(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                {typing.admin && (
                  <div className="flex justify-start">
                    <div className="bg-blue-50 rounded-lg px-4 py-2">
                      <p className="text-sm text-gray-600 italic">
                        {typing.adminName || 'Support Team'} is typing...
                      </p>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex space-x-2">
              <input
                type="text"
                value={messageText}
                onChange={(e) => {
                  setMessageText(e.target.value);
                  handleTyping();
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder={
                  messages.length === 0
                    ? 'Type your message to start...'
                    : 'Type your message...'
                }
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              />
              <button
                onClick={handleSendMessage}
                disabled={!messageText.trim()}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:bg-purple-300 disabled:cursor-not-allowed flex items-center"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
